// src/api/index.ts
import { invalidateCache } from '~/utils/issues/requestCache'

export enum IssuesArtifactType {
  Skill = 'SKILL',
}

export const API_BASE = '/api/issues'

// === SKILL API ===

export interface SkillInstallationInput {
  entity_type: string
  entity_id: string | null
  mono_repo_config_id?: string
}

export async function createSkillFromZip(file: File, installations: SkillInstallationInput[]): Promise<{ id: string }> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('installations', JSON.stringify(installations))

  const res = await fetch(`/api/skills/assets`, {
    method: 'POST',
    body: formData,
  })

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}))
    throw new Error(errorData.error || 'Failed to create skill from zip')
  }

  invalidateCache(`skills?`)

  const uploadResult = await res.json()
  if (!uploadResult.success) {
    throw new Error(uploadResult.error || 'Failed to create skill from zip')
  }

  const skillId = uploadResult.asset.skill_id
  invalidateCache(`skills/${skillId}`)

  return { id: skillId }
}

// ===============================================================================
// UNIFIED ORCHESTRATION API - Phase 8 Implementation
// ===============================================================================

/**
 * Generic orchestration function for all StreamableObject types.
 * Replaces all domain-specific chat/orchestration endpoints.
 */
export function orchestrateUniversal(
  domainType: string,
  domainId: string,
  message?: string,
  options: Record<string, any> = {},
): Promise<{ success: boolean; stream_id: string; domain_type: string; display_name: string }> {
  return fetch(`${API_BASE}/${domainType}/${domainId}/orchestrate/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, ...options }),
  }).then((r) => {
    if (!r.ok) throw new Error(`Orchestration failed for ${domainType}/${domainId}`)
    return r.json()
  })
}

/**
 * Universal streaming function for all orchestrators.
 * Works with the unified streaming endpoint that handles all StreamableObject types.
 */
export function streamUniversalEvents(
  streamId: string, // Already in format: domain_type_id (e.g., session_abc123, spec_xyz789)
  onMessage: (event: any) => void,
  lastEventIndex: number = 0,
): EventSource {
  const url = lastEventIndex
    ? `${API_BASE}/streams/${streamId}/stream/?last_event_index=${lastEventIndex}`
    : `${API_BASE}/streams/${streamId}/stream/`

  const es = new EventSource(url)
  es.onmessage = (event) => {
    try {
      const parsed = JSON.parse(event.data)
      onMessage(parsed)
    } catch (e) {
      console.error('Failed to parse universal stream event:', e)
    }
  }
  es.onerror = (error) => {
    console.error('Universal stream error:', error)
  }
  return es
}

/**
 * Stop orchestration for any domain type.
 */
export function stopUniversalOrchestration(
  domainType: string,
  domainId: string,
): Promise<{ success: boolean; message: string; stoppedOrchestrationId?: string }> {
  return fetch(`${API_BASE}/${domainType}/${domainId}/stop/`, {
    method: 'POST',
  }).then((r) => {
    if (!r.ok) throw new Error(`Failed to stop orchestration for ${domainType}/${domainId}`)
    return r.json()
  })
}
